import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RepositoryBase } from '@asm/features/sync/repositories/repository-base';

/**
 * Estrutura de uma condição de promoção serializada para o IndexedDB.
 * - Condições do tipo MODEL têm `values[]` com os pares {model, code}
 *   que o motor avalia via dynamicFilters (sem query no banco).
 * - Condições do tipo scalar (ex: ORDER_TOTAL_VALUE) têm `value` numérico
 *   e `operatorCode` para comparação.
 */
export interface LocalPromotionCondition {
  id: string;
  conditionType: string;
  value?: number;
  operatorCode?: string;
  values: Array<{ model: string; code: string }>;
}

/**
 * Estrutura de uma ação de promoção serializada para o IndexedDB.
 * O campo `discountValue` é usado para ações numéricas diretas.
 * Para ações do tipo MODEL, o valor está em `values[0].kValue`.
 */
export interface LocalPromotionAction {
  id: string;
  actionType: string;
  discountValue?: number;
  calculationType: 'A' | 'B' | 'C'; // A=%, B=Fixo, C=Quantidade
  conditionClass: 'A' | 'B' | 'C'; // A=Desconto, B=Acréscimo, C=Imposto
  baseConditionKey?: string;
  values: Array<{ model: string; code: string; kValue?: number }>;
}

/**
 * Promoção serializada para armazenamento offline no IndexedDB.
 *
 * Todas as condições e ações são desnormalizadas (embutidas no objeto)
 * para permitir avaliação sem joins adicionais no modo offline.
 */
export interface LocalPromotion {
  id: string;
  code: string;
  description: string;
  priority: number;
  active: boolean;
  startDate: string; // ISO 8601 date string — "2025-01-01"
  endDate: string; // ISO 8601 date string — "2099-12-31"
  sapConditionType: string; // ex: "ZPRO", "ZDPC"
  message?: string;
  applyAutomatically: boolean;
  conditionGroupOperator: 'AND' | 'OR';
  conditions: LocalPromotionCondition[];
  actions: LocalPromotionAction[];
}

/**
 * Repositório IndexedDB para promoções offline.
 *
 * Segue o mesmo padrão dos demais repositories do módulo sync:
 * estende `RepositoryBase<LocalPromotion>` e expõe métodos de conveniência.
 *
 * O store `promotions` é populado durante a sincronização inbound
 * pelo apiCode `Promotions` (vide sync.config.ts).
 */
@Injectable({ providedIn: 'root' })
export class PromotionRepository extends RepositoryBase<LocalPromotion> {
  protected readonly KEY_PATH = 'promotions';

  /**
   * Retorna todas as promoções cujo intervalo de datas cobre hoje.
   *
   * A filtragem é feita no cliente (não há índice de data no IndexedDB)
   * para evitar complexidade de migração de schema. O volume de promoções
   * ativas é tipicamente pequeno, tornando a filtragem em memória aceitável.
   */
  getActivePromotions(): Observable<LocalPromotion[]> {
    const today = new Date().toISOString().split('T')[0];
    return this.getAll().pipe(
      map(promotions => promotions.filter(p => p.active && p.startDate <= today && p.endDate >= today)),
    );
  }
}
